import { useEffect, useMemo, useRef, useState } from 'react';

const MAX_AUDIO_BARS = 8;
const CLOUDINARY_AUDIO_HOST = 'res.cloudinary.com';
const mediaSourceGraphs = new WeakMap();

const normalizeBarCount = (value) =>
  Math.max(1, Math.min(MAX_AUDIO_BARS, Number(value) || 5));

export const clampRadioAudioLevel = (value) => {
  const level = Number(value);
  if (!Number.isFinite(level)) return 0;
  return Math.max(0, Math.min(1, level));
};

const getWindowLocationOrigin = () => {
  if (typeof window === 'undefined') return '';
  return window.location?.origin || '';
};

const parseAudioUrl = (audioUrl) => {
  const value = String(audioUrl || '').trim();
  if (!value) return null;
  try {
    return new URL(value, typeof window !== 'undefined' ? window.location.href : undefined);
  } catch (err) {
    return null;
  }
};

export const canAnalyzeRadioAudioUrl = (audioUrl) => {
  const parsed = parseAudioUrl(audioUrl);
  if (!parsed) return false;
  const origin = getWindowLocationOrigin();
  if (origin && parsed.origin === origin) return true;
  return parsed.hostname.toLowerCase() === CLOUDINARY_AUDIO_HOST;
};

export const canAnalyzeRadioAudioQueue = (queue = []) => {
  const items = Array.isArray(queue) ? queue : [];
  const playableUrls = items
    .map((item) => String(item?.audioUrl || '').trim())
    .filter(Boolean);
  return playableUrls.length > 0 && playableUrls.every(canAnalyzeRadioAudioUrl);
};

export const getRadioAudioCrossOrigin = (audioUrl) => {
  const parsed = parseAudioUrl(audioUrl);
  if (!parsed || !canAnalyzeRadioAudioUrl(audioUrl)) return undefined;
  const origin = getWindowLocationOrigin();
  if (origin && parsed.origin === origin) return undefined;
  return 'anonymous';
};

export const buildRadioAudioIntensityStyle = (intensity = 0) => {
  const level = clampRadioAudioLevel(intensity);
  return {
    '--radio-audio-intensity': level.toFixed(3),
    '--radio-art-glow': `${Math.round(10 + level * 28)}px`,
    '--radio-art-glow-opacity': (0.12 + level * 0.28).toFixed(3),
    '--radio-art-scale': (1 + level * 0.014).toFixed(3),
  };
};

const getAudioContextClass = () => {
  if (typeof window === 'undefined') return null;
  return window.AudioContext || window.webkitAudioContext || null;
};

const prefersReducedMotion = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return Boolean(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (err) {
    return false;
  }
};

const getFrameApi = () => {
  if (
    typeof window !== 'undefined' &&
    typeof window.requestAnimationFrame === 'function' &&
    typeof window.cancelAnimationFrame === 'function'
  ) {
    return {
      request: window.requestAnimationFrame.bind(window),
      cancel: window.cancelAnimationFrame.bind(window),
    };
  }
  return {
    request: (callback) => window.setTimeout(() => callback(Date.now()), 33),
    cancel: (frameId) => window.clearTimeout(frameId),
  };
};

const getOrCreateMediaSourceGraph = (audio) => {
  const existing = mediaSourceGraphs.get(audio);
  if (existing && existing.context?.state !== 'closed') return existing;

  const AudioContextClass = getAudioContextClass();
  if (!AudioContextClass) return null;

  const context = new AudioContextClass();
  const source = context.createMediaElementSource(audio);
  source.connect(context.destination);

  const graph = { context, source };
  mediaSourceGraphs.set(audio, graph);
  return graph;
};

const sampleAudioLevels = (analyser, dataArray, barCount) => {
  analyser.getByteFrequencyData(dataArray);
  const count = normalizeBarCount(barCount);
  const bucketSize = Math.max(1, Math.floor(dataArray.length / count));
  let total = 0;
  const levels = Array.from({ length: count }, (_, index) => {
    const start = index * bucketSize;
    const end =
      index === count - 1
        ? dataArray.length
        : Math.min(dataArray.length, start + bucketSize);
    let bucketTotal = 0;
    let bucketCount = 0;
    for (let cursor = start; cursor < end; cursor += 1) {
      const value = Number(dataArray[cursor]) || 0;
      bucketTotal += value;
      total += value;
      bucketCount += 1;
    }
    const average = bucketCount ? bucketTotal / bucketCount : 0;
    return clampRadioAudioLevel(Math.pow(average / 255, 0.72));
  });
  const intensity = dataArray.length
    ? clampRadioAudioLevel(total / dataArray.length / 255)
    : 0;
  return { levels, intensity };
};

const buildFallbackState = (barCount, fallbackReason) => ({
  levels: [],
  intensity: 0,
  isBeatReactive: false,
  fallbackReason,
  barCount: normalizeBarCount(barCount),
});

export default function useRadioAudioLevels(
  audioRef,
  { audioUrl = '', isPlaying = false, barCount = 5, enabled = true } = {},
) {
  const safeBarCount = normalizeBarCount(barCount);
  const [audioState, setAudioState] = useState(() =>
    buildFallbackState(safeBarCount, 'idle'),
  );
  const canAnalyze = useMemo(
    () => enabled && canAnalyzeRadioAudioUrl(audioUrl),
    [audioUrl, enabled],
  );

  useEffect(() => {
    if (!isPlaying) {
      setAudioState(buildFallbackState(safeBarCount, 'idle'));
      return undefined;
    }
    if (!enabled) {
      setAudioState(buildFallbackState(safeBarCount, 'disabled'));
      return undefined;
    }
    if (!canAnalyze) {
      setAudioState(buildFallbackState(safeBarCount, 'unsafe-url'));
      return undefined;
    }
    if (prefersReducedMotion()) {
      setAudioState(buildFallbackState(safeBarCount, 'reduced-motion'));
      return undefined;
    }

    const audio = audioRef?.current;
    const frameApi = getFrameApi();
    if (!audio) {
      setAudioState(buildFallbackState(safeBarCount, 'missing-audio'));
      return undefined;
    }

    let analyser = null;
    let dataArray = null;
    let frameId = null;
    let cancelled = false;
    let lastSampleAt = 0;
    let graph = null;

    try {
      graph = getOrCreateMediaSourceGraph(audio);
      if (!graph?.context || !graph?.source) {
        setAudioState(buildFallbackState(safeBarCount, 'unsupported'));
        return undefined;
      }
      if (graph.context.state === 'suspended') {
        graph.context.resume?.().catch?.(() => {});
      }
      analyser = graph.context.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.76;
      dataArray = new Uint8Array(analyser.frequencyBinCount);
      graph.source.connect(analyser);
    } catch (err) {
      setAudioState(buildFallbackState(safeBarCount, 'analyser-failed'));
      return undefined;
    }

    const loop = (timestamp = 0) => {
      if (cancelled || !analyser || !dataArray) return;
      const nextTimestamp = Number(timestamp) || Date.now();
      if (!lastSampleAt || nextTimestamp - lastSampleAt >= 32) {
        lastSampleAt = nextTimestamp;
        const sample = sampleAudioLevels(analyser, dataArray, safeBarCount);
        setAudioState({
          levels: sample.levels,
          intensity: sample.intensity,
          isBeatReactive: true,
          fallbackReason: '',
          barCount: safeBarCount,
        });
      }
      frameId = frameApi.request(loop);
    };

    frameId = frameApi.request(loop);

    return () => {
      cancelled = true;
      if (frameId !== null) {
        frameApi.cancel(frameId);
      }
      if (graph?.source && analyser) {
        try {
          graph.source.disconnect(analyser);
        } catch (err) {
          // The direct source->destination connection remains intact for playback.
        }
      }
    };
  }, [audioRef, audioUrl, canAnalyze, enabled, isPlaying, safeBarCount]);

  return audioState;
}
